// src/components/ConfigList.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { SetFilterModule } from "ag-grid-enterprise";
import {
  PamGrid,
  GridConfigMenu,
  useGridConfigs,
  useNotification,
  BooleanRenderer,
  DateRenderer,
  createActionsColumn,
  notifyPermissionError,
} from '@1823-partners/core';
import api, { fetchConfigs, runScenario } from "../api";
import CreateConfigModal from "./CreateConfigModal";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { notifyUngatedFailure } from '../utils/notifyDenial';

dayjs.extend(utc);
dayjs.extend(timezone);

const ConfigList = ({ onSelectConfig, userId, onLoadAnalysis, onSubmitted }) => {
  const { showNotification } = useNotification();
  const [rowData, setRowData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const gridRef = useRef(null);

  // Per-user grid layout persistence. We own the hook here (rather than
  // letting GridConfigMenu instantiate it internally) so we can bridge
  // gridConfigs.onGridReady into PamGrid's onGridReady — without that bridge a
  // slow grid that mounts after the configs response would miss the
  // default-layout auto-apply. gridId is unique within the scenarios app.
  const gridConfigs = useGridConfigs(api, {
    app: "scenarios",
    gridId: "configs",
    gridApiRef: gridRef,
  });

  const loadConfigs = async () => {
    try {
      const response = await fetchConfigs();
      setRowData(response.data);
    } catch (error) {
      console.error("Error fetching configs", error);
      notifyUngatedFailure(showNotification, error);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  const handleRunScenario = async (configId) => {
    try {
      await runScenario(configId);
      onSubmitted?.(configId);
      showNotification("Scenario jobs submitted for config " + configId, "success");
      loadConfigs();
    } catch (error) {
      console.error("Error running scenario", error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification("Failed to run scenario", "error");
      }
    }
  };

  const handleEditConfig = (configData) => {
    setEditingConfig(configData);
    setModalOpen(true);
  };

  const handleLoadAnalysis = (configData) => {
    if (onLoadAnalysis) {
      onLoadAnalysis(configData);
    }
  };

  const timeFilter = {
    browserDatePicker: true,
    comparator: (filterDate, cellValue) => {
      if (!cellValue) return -1;
      const d = new Date(cellValue);
      if (d < filterDate) return -1;
      if (d > filterDate) return 1;
      return 0;
    },
    inRangeInclusive: true
  };

  // Timezone-specific datetime formatter (domain-specific, not in core)
  const modifiedDateFormatter = (params) => {
    if (!params.value) return '';
    const date = dayjs(params.value);
    return date.tz("America/New_York").format("M/D/YYYY h:mm:ss A");
  };

  const columnDefs = [
    { headerName: "ID", field: "ID", filter: 'agSetColumnFilter', enableCellTextSelection: true },
    { headerName: "Scenarios", field: "SCEN_IDS", enableCellTextSelection: true },
    { headerName: "CUSIPs", field: "CUSIPS", enableCellTextSelection: true },
    {
      headerName: "Asof Date",
      field: "ASOFDT",
      cellRenderer: DateRenderer,
      filterParams: timeFilter,
      filter: 'agDateColumnFilter',
      enableCellTextSelection: true
    },
    { headerName: "Environment", field: "ENV", filter: 'agSetColumnFilter', enableCellTextSelection: true },
    { headerName: "Description", field: "DESCRIPTION", enableCellTextSelection: true },
    { headerName: "Book", field: "BOOK", filter: 'agSetColumnFilter', enableCellTextSelection: true },
    {
      headerName: "Skip Intex",
      field: "SKIP_INTEX",
      cellRenderer: BooleanRenderer,
      filter: 'agSetColumnFilter',
      enableCellTextSelection: true
    },
    {
      headerName: "Skip ADCO",
      field: "SKIP_ADCO",
      cellRenderer: BooleanRenderer,
      filter: 'agSetColumnFilter',
      enableCellTextSelection: true
    },
    {
      headerName: "Intex ADCO Only",
      field: "IA_ONLY",
      cellRenderer: BooleanRenderer,
      filter: 'agSetColumnFilter',
      enableCellTextSelection: true
    },
    {
      headerName: "Hello World",
      field: "HELLO_WORLD",
      cellRenderer: BooleanRenderer,
      filter: 'agSetColumnFilter',
      enableCellTextSelection: true
    },
    { headerName: "Custom PAM-MASTER Branch", field: "CUSTOM_PM_BRANCH", enableCellTextSelection: true },
    { headerName: "Custom PAM-KWA Branch", field: "CUSTOM_PKWA_BRANCH", enableCellTextSelection: true },
    {
      headerName: "Save Results",
      field: "SAVE",
      cellRenderer: BooleanRenderer,
      enableCellTextSelection: true
    },
    { headerName: "Modified By", field: "MODIFIED_BY", enableCellTextSelection: true },
    {
      headerName: "Modified Date",
      field: "MODIFIED_DATE",
      valueFormatter: modifiedDateFormatter,
      filterParams: timeFilter,
      filter: 'agDateColumnFilter',
      enableCellTextSelection: true
    },
    createActionsColumn({
      actions: [
        { key: 'load', icon: FolderOpenIcon, tooltip: 'Load Portfolio', color: 'info', onClick: (data) => handleLoadAnalysis(data) },
        { key: 'run', icon: PlayArrowIcon, tooltip: 'Run Scenario', color: 'success', onClick: (data) => handleRunScenario(data.ID) },
        'edit',
        { key: 'viewRuns', icon: VisibilityIcon, tooltip: 'View Runs', onClick: (data) => onSelectConfig && onSelectConfig(data.ID) },
      ],
      onEdit: (data) => handleEditConfig(data),
    }),
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <Typography variant="h5">Scenario Configs</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />
          <Button
            sx={{ backgroundColor: "lightgrey", color: "black", m: 1 }}
            onClick={() => {
              setEditingConfig(null);
              setModalOpen(true);
            }}
          >
            New Config
          </Button>
        </Box>
      </Box>
      <PamGrid
        ref={gridRef}
        modules={[SetFilterModule]}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={{ flex: 1, filter: 'agTextColumnFilter', sortable: true, resizable: true }}
        getRowId={(data) => `${data.ID}`}
        height={400}
        showQuickFilter={false}
        showFooter={false}
        // Per-user grid layout persistence. Bridge onGridReady so the saved
        // default applies even if the grid mounts after configs load; mirror
        // every column/sort/filter gesture into the local stash. PamGrid
        // surfaces onFilterChanged at the top level; the column events ride
        // through agGridProps as the stashBindings bundle.
        onGridReady={gridConfigs.onGridReady}
        onFilterChanged={gridConfigs.onGridStateChanged}
        agGridProps={gridConfigs.stashBindings}
      />
      <CreateConfigModal
        open={modalOpen}
        onCreated={loadConfigs}
        onClose={() => setModalOpen(false)}
        userId={userId}
        initialData={editingConfig}
      />
    </Box>
  );
};

export default ConfigList;
